import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const LOADING_STALL_MS = 30_000;

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, onboardingProgress, subscription, sessionExpired } = useAuth();
  const location = useLocation();
  const [stalled, setStalled] = useState(false);

  // Treat as still loading only while auth is hydrating. Once auth resolves, missing
  // onboarding rows shouldn't trap the user in a perpetual spinner — redirect below.
  const isStillLoading = isLoading;

  // Diagnostic logging for stuck loading states
  useEffect(() => {
    console.log('[ProtectedRoute] isLoading:', isLoading, 'onboardingProgress:', !!onboardingProgress, 'isAuthenticated:', isAuthenticated, 'sessionExpired:', sessionExpired);
  }, [isLoading, onboardingProgress, isAuthenticated, sessionExpired]);

  // Non-destructive fallback: after 30s of loading, show a refresh prompt
  useEffect(() => {
    if (!isStillLoading) {
      setStalled(false);
      return;
    }

    const timer = setTimeout(() => setStalled(true), LOADING_STALL_MS);
    return () => clearTimeout(timer);
  }, [isStillLoading]);

  // Genuine first load — show spinner
  if (isStillLoading && !stalled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Loading stalled — non-destructive prompt
  if (isStillLoading && stalled) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center">
        <p className="text-muted-foreground">Loading is taking longer than expected.</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Refresh page
        </button>
      </div>
    );
  }

  // Session expired - redirect to session expired page
  if (!isAuthenticated && sessionExpired) {
    return <Navigate to="/session-expired" state={{ from: location }} replace />;
  }

  // Not authenticated (includes timeout fallback) - redirect to auth
  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Now safe to check onboarding (data is guaranteed loaded)
  if (!onboardingProgress?.plan_selected) {
    return <Navigate to="/onboarding/plan" replace />;
  }

  // Business plan but team invite not completed - redirect to team invite
  if (
    subscription?.plan === 'business' &&
    !onboardingProgress.team_invite_completed &&
    !onboardingProgress.onboarding_completed
  ) {
    return <Navigate to="/onboarding/team" replace />;
  }

  // All checks passed - render children
  return <>{children}</>;
}
